'use client';

/**
 * CopyableId — standalone copy component for IDs in table rows.
 *
 * This is a full copy of Copyable with two differences:
 *
 * 1. Container-level click handler with preventDefault + stopPropagation.
 *    Copyable puts the click handler on the icon button only. CopyableId
 *    puts it on the entire container so clicks anywhere on the ID text
 *    trigger a copy. preventDefault/stopPropagation are needed because
 *    this component lives inside DataTable rows wrapped in <a> tags
 *    (via rowHref) — without them, clicking the ID would navigate away.
 *
 * 2. Icon only visible on hover. The copy icon is hidden by default and
 *    appears when the user hovers over the component, keeping the table
 *    rows visually clean.
 *
 * Styling is fixed to font-mono text-xs (ID display), and a toast is
 * shown on copy. Consider porting these behaviors to Copyable in a
 * future iteration.
 */

import { useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { copyToClipboard, truncateText } from '@bridge/utils';
import toast from 'react-hot-toast';

import { FlexContainer } from './FlexContainer';

import { faClone } from '@icons/classic/regular';

/** Middle-ellipsis length used when `truncate` is `true` (e.g. `b1c4cc84...d2ec545`). */
const DEFAULT_TRUNCATE_LENGTH = 15;

export interface CopyableIdProps {
  children: string;
  className?: string;
  /** Always show the copy icon instead of only on hover */
  alwaysShowIcon?: boolean;
  /**
   * Middle-ellipsize the displayed id while copying the full value. `true` uses a
   * default length of 15; pass a number for a custom length. Ids shorter than the
   * length render in full.
   */
  truncate?: boolean | number;
}

export function CopyableId({ children, className, alwaysShowIcon, truncate }: CopyableIdProps) {
  const textRef = useRef<HTMLSpanElement>(null);

  const displayText = truncate
    ? truncateText(children, typeof truncate === 'number' ? truncate : DEFAULT_TRUNCATE_LENGTH, { middle: true })
    : children;

  const handleClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const textToCopy = children || textRef.current?.textContent || '';
    const success = await copyToClipboard(textToCopy);
    if (success) {
      toast.success('Copied to clipboard');
    } else {
      toast.error('Failed to copy');
    }
  };

  return (
    <FlexContainer
      className="gap-1 inline-flex h-fit cursor-pointer group"
      items="center"
      onClick={handleClick}
    >
      <span className={className ?? 'font-mono text-xs'} ref={textRef}>
        {displayText}
      </span>
      <span className={`text-content-secondary ${alwaysShowIcon ? '' : 'opacity-0 group-hover:opacity-100'} transition-opacity`}>
        <FontAwesomeIcon icon={faClone} size="xs" />
      </span>
    </FlexContainer>
  );
}
